import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import {
  FunnelStatus,
  IFunnelAddress,
  IFunnelItem,
  IFunnelTimeSlot,
  INewOrderState,
  PaymentMode,
} from "./types";

export const newOrderInitialState: INewOrderState = {
  items: [],
  months: 1,
  address: {},
  couponDiscount: 0,
  status: "idle",
};

const newOrderSlice = createSlice({
  name: "newOrder",
  initialState: newOrderInitialState,
  reducers: {
    startOrder: () => ({ ...newOrderInitialState }),
    setItemQuantity: (
      state,
      { payload }: PayloadAction<{ storagetypeId: number; quantity: number }>
    ) => {
      const { storagetypeId, quantity } = payload;
      const existing = state.items.find(
        (i) => i.storagetypeId === storagetypeId
      );
      if (quantity <= 0) {
        state.items = state.items.filter(
          (i) => i.storagetypeId !== storagetypeId
        );
        return;
      }
      if (existing) {
        existing.quantity = quantity;
      } else {
        state.items.push({ storagetypeId, quantity });
      }
    },
    setMonths: (state, { payload }: PayloadAction<number>) => {
      state.months = payload;
    },
    setPrepayMonths: (state, { payload }: PayloadAction<number>) => {
      state.prepayMonths = payload;
    },
    setOrderDate: (state, { payload }: PayloadAction<string | undefined>) => {
      state.requestDate = payload;
    },
    setOrderTimeSlot: (
      state,
      { payload }: PayloadAction<IFunnelTimeSlot | undefined>
    ) => {
      state.timeSlot = payload;
    },
    setOrderAddress: (state, { payload }: PayloadAction<IFunnelAddress>) => {
      state.address = { ...state.address, ...payload };
    },
    setPaymentMode: (state, { payload }: PayloadAction<PaymentMode>) => {
      state.paymentMode = payload;
    },
    applyCoupon: (
      state,
      {
        payload,
      }: PayloadAction<{ code: string; id?: number; discount?: number }>
    ) => {
      state.couponCode = payload.code;
      state.couponId = payload.id;
      state.couponDiscount = payload.discount ?? 0;
    },
    removeCoupon: (state) => {
      state.couponCode = undefined;
      state.couponId = undefined;
      state.couponDiscount = 0;
    },
    setOrderComment: (state, { payload }: PayloadAction<string>) => {
      state.comment = payload;
    },
    setContact: (
      state,
      {
        payload,
      }: PayloadAction<{ name?: string; email?: string; phone?: string }>
    ) => {
      if (payload.name !== undefined) state.name = payload.name;
      if (payload.email !== undefined) state.email = payload.email;
      if (payload.phone !== undefined) state.phone = payload.phone;
    },
    setCountryId: (state, { payload }: PayloadAction<number | undefined>) => {
      state.countryId = payload;
    },
    setAssigneeId: (state, { payload }: PayloadAction<number | undefined>) => {
      state.assigneeId = payload;
    },
    setStatus: (state, { payload }: PayloadAction<FunnelStatus>) => {
      state.status = payload;
    },
    setCreatedOrderId: (state, { payload }: PayloadAction<number>) => {
      state.createdOrderId = payload;
    },
    setTotalRent: (state, { payload }: PayloadAction<number>) => {
      state.totalRent = payload;
    },
    resetOrder: () => ({ ...newOrderInitialState }),
  },
});

export const {
  startOrder,
  setItemQuantity,
  setMonths,
  setPrepayMonths,
  setOrderDate,
  setOrderTimeSlot,
  setOrderAddress,
  setPaymentMode,
  applyCoupon,
  removeCoupon,
  setOrderComment,
  setContact,
  setCountryId,
  setAssigneeId,
  setStatus,
  setCreatedOrderId,
  setTotalRent,
  resetOrder,
} = newOrderSlice.actions;

export const selectNewOrder = (state: RootState): INewOrderState =>
  state.newOrder;
export const selectNewOrderItems = (state: RootState): IFunnelItem[] =>
  state.newOrder.items;
export const selectNewOrderItemCount = (state: RootState): number =>
  state.newOrder.items.reduce((sum, i) => sum + i.quantity, 0);

export default newOrderSlice.reducer;
